/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Wed, Jul 21st 2021, 5:35:25 pm
 *
 * *****************************************************
 */

import { useCallback, useEffect, useState, useRef } from 'react';

import { TextStyle, Link, Banner } from '@shopify/polaris';
import { FormApi } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { Redirect, useHistory } from 'react-router-dom';

import {
  SIGN_IN_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION,
  SIGN_UP_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION
} from 'constants/recaptcha-actions';
import {
  CREATE_WORKSPACE,
  GET_STARTED_NEW,
  SELECT_WORKSPACE,
  SIGN_IN
} from 'features/auth/routes/paths';
import PageErrorBanner from 'features/common/views/components/PageErrorBanner';
import { authAPI } from 'states/services/auth';
import {
  useSignInWithEmailPasswordMutation,
  useSignUpWithEmailPasswordMutation,
  useConfirmEmailSignInMutation,
  useConfirmEmailSignUpMutation
} from 'states/services/auth/hooks';
import {
  AuthenticationEmailPasswordRequest,
  ConfirmEmailRequest
} from 'states/services/auth/types';
import {
  ErrorObject,
  convertApiErrorsToFinalFormSubmitErrors,
  SubmissionResponse
} from 'utils/form/convert';
import { deformatCode, formatCode } from 'utils/form/format';
import { composeValidators, verificationCode, required } from 'utils/form/validation';
import { useAppDispatch, useGoogleReCaptchaV3 } from 'utils/hooks';

import BackButton from '../../components/BackButton';
import AuthLayout from '../../layouts/AuthLayout';
import { H1, ContentContainer, Footer } from './styles';
import TokenFieldAdapter from './TokenFieldAdapter';
import TokenVerifyLoading from './TokenVerifyLoading';

type Props = {
  location: {
    pathname: string;
    state?: {
      referrer?: string | null;
      email?: string | null;
    };
  };
};

const VerifyEmail = ({ location }: Props): JSX.Element => {
  const { state } = location;
  const { email, referrer } = state ?? {};
  const isSignIn = referrer === SIGN_IN;
  /**
   * pageError is our single souce of true for page error
   */
  const [pageError, setPageError] = useState<ErrorObject | undefined>(undefined);

  /**
   * [apiTrigger, apiResult] of RTK Mutation
   */
  const [confirmEmailSignIn, confirmEmailSignInStatus] = useConfirmEmailSignInMutation();
  const [confirmEmailSignUp, confirmEmailSignUpStatus] = useConfirmEmailSignUpMutation();
  const [signInWithEmailPassword, signInWithEmailPasswordStatus] =
    useSignInWithEmailPasswordMutation();
  const [signUpWithEmailPassword, signUpWithEmailPasswordStatus] =
    useSignUpWithEmailPasswordMutation();

  const confirmEmailIsLoading =
    confirmEmailSignInStatus.isLoading || confirmEmailSignUpStatus.isLoading;
  const confirmEmailError = confirmEmailSignInStatus.error ?? confirmEmailSignUpStatus.error;

  const resendEmailIsLoading =
    signInWithEmailPasswordStatus.isLoading || signUpWithEmailPasswordStatus.isLoading;
  const resendEmailIsSuccess =
    signInWithEmailPasswordStatus.isSuccess || signUpWithEmailPasswordStatus.isSuccess;

  const { isReady: isReCaptchaReady, execute: executeReCaptcha } = useGoogleReCaptchaV3();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const formRef = useRef<FormApi | null>(null);

  /**
   * Reset API and page errors
   */
  const handleResetError = useCallback(() => {
    setPageError(undefined);
    dispatch(authAPI.util.resetApiState());
  }, [dispatch]);

  const handleFormSubmit = useCallback(
    async values => {
      handleResetError();

      /**
       * Pick the suitable API
       */
      let confirmEmail = null;
      if (isSignIn) confirmEmail = confirmEmailSignIn;
      else confirmEmail = confirmEmailSignUp;

      if (confirmEmail) {
        /**
         * Prepare the request data
         */
        const { token } = values;
        const data: ConfirmEmailRequest = {
          email: email!,
          token
        };
        /**
         * Make the request
         */
        const res = (await confirmEmail(data)) as SubmissionResponse;
        if (res.error) {
          /**
           * Return the submission errors for displaying (if any)
           */
          return convertApiErrorsToFinalFormSubmitErrors(res.error);
        }
      }
      return undefined; // undefined means no submission errors
    },
    [email, isSignIn, confirmEmailSignIn, confirmEmailSignUp, handleResetError]
  );

  const handleCodeResending = useCallback(async () => {
    handleResetError();

    /**
     * Get the captcha token
     */
    if (!isReCaptchaReady) {
      const errorObject = {
        data: {
          message: 'Execute recaptcha not yet available. Please try again!'
        }
      };
      /**
       * Display captcha error
       */
      setPageError(errorObject);
      return;
    }

    const captchaToken = await executeReCaptcha(
      isSignIn
        ? SIGN_IN_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION
        : SIGN_UP_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION
    );

    let resend = null;
    if (isSignIn) resend = signInWithEmailPassword;
    else resend = signUpWithEmailPassword;

    if (resend !== null) {
      /**
       * Prepare the request data
       */
      const data: AuthenticationEmailPasswordRequest = {
        strategy: 'email_password',
        email: email!,
        captchaToken
      };
      /**
       * Make the request
       */
      await resend(data);
      /**
       * No returned submission errors here
       * because this request is not a submission one
       */
    }
  }, [
    email,
    isReCaptchaReady,
    executeReCaptcha,
    isSignIn,
    signInWithEmailPassword,
    signUpWithEmailPassword,
    handleResetError
  ]);

  const handleBackButton = useCallback(() => {
    history.push(referrer!);
  }, [referrer, history]);

  useEffect(() => {
    /**
     * pageError syncs with API error
     */
    const error =
      signUpWithEmailPasswordStatus.error ??
      signInWithEmailPasswordStatus.error ??
      confirmEmailError;
    setPageError(error as ErrorObject);
  }, [signUpWithEmailPasswordStatus.error, signInWithEmailPasswordStatus.error, confirmEmailError]);

  useEffect(() => {
    if (formRef && formRef.current) {
      if (resendEmailIsLoading) {
        /**
         * Reset form when resending email
         */
        formRef.current?.restart();
      }
    }
  }, [resendEmailIsLoading, formRef]);

  if (!email) return <Redirect to={SIGN_IN} />;

  if (confirmEmailSignInStatus.isSuccess || confirmEmailSignUpStatus.isSuccess) {
    if (isSignIn) {
      return <Redirect to={SELECT_WORKSPACE} />;
    }
    return (
      <Redirect
        to={{
          pathname: CREATE_WORKSPACE,
          state: { referrer: GET_STARTED_NEW }
        }}
      />
    );
  }

  return (
    <AuthLayout>
      <Helmet>
        <title>Confirm code | WiseIO</title>
        <meta name="description" content="Confirm code" />
        <meta property="og:url" content={window.location.href} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <BackButton onClick={handleBackButton} />
      <header>
        <H1>Enter confirmation code</H1>
        <span>
          <TextStyle variation="subdued">
            We have sent you a 6-character code to <br />
            <TextStyle variation="strong">{email}</TextStyle>. Please enter it here to confirm.
          </TextStyle>
        </span>
      </header>
      <ContentContainer>
        {resendEmailIsSuccess && (
          <Banner status="success" title="A new code has been sent to your email." />
        )}
        {pageError && <PageErrorBanner data={pageError} />}
        <FinalForm
          onSubmit={handleFormSubmit}
          render={({ form, handleSubmit, submitting }) => {
            formRef.current = form;
            return (
              <form onSubmit={handleSubmit}>
                <Field
                  name="token"
                  component={TokenFieldAdapter}
                  label="Verification code"
                  disabled={
                    submitting ||
                    signInWithEmailPasswordStatus.isLoading ||
                    signUpWithEmailPasswordStatus.isLoading
                  }
                  labelHidden
                  placeholder="XXX XXX"
                  parse={deformatCode}
                  format={formatCode}
                  submitForm={handleSubmit}
                  validate={composeValidators(required('Verification code'), verificationCode)}
                />
              </form>
            );
          }}
        />
      </ContentContainer>
      <Footer confirmEmailIsLoading={confirmEmailIsLoading}>
        {confirmEmailIsLoading ? (
          <TokenVerifyLoading />
        ) : (
          <TextStyle variation="subdued">
            Didn’t get any code? {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link removeUnderline onClick={handleCodeResending}>
              Resend a new one.
            </Link>
          </TextStyle>
        )}
      </Footer>
    </AuthLayout>
  );
};

export default VerifyEmail;
